/**
 * sectionTutorialSlides.js
 *
 * Tutorial slide content for every onboarding section.
 * Each key maps to a slideGroup used by TutorialDialog.
 * Triggered after the user completes the corresponding onboarding step.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  CloudUpload, Visibility, ViewInAr, Edit, History, Straighten, Draw, ContentCut, Fullscreen, SmartToy, TableChart,
  Description, Checklist, VerifiedUser,
  Assessment, Chat, AttachFile, Psychology, Lightbulb,
  Dashboard, Gavel, Download,
  AutoAwesome, PlayArrow, AccountTree, Settings,
  FolderSpecial, DocumentScanner, FactCheck, Speed,
  ManageSearch, LibraryBooks, Hub, DataObject, } from '@mui/icons-material';

// ── Shared helper components ──────────────────────────────────

function FeatureRow({ icon, title, color, items }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Box>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ml: 0.5, mt: 0.3 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: color, mt: 0.9, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function FeatureCard({ icon, title, desc, color }) {
  return (
    <Box sx={{ flex: 1, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">{desc}</Typography>
    </Box>
  );
}

function TabCard({ label, color, desc }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="caption" display="block" color="text.secondary">{desc}</Typography>
      </Box>
    </Box>
  );
}

function Tip({ text }) {
  return (
    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(102,126,234,0.06)', borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary">
        <strong>Tip:</strong> {text}
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════
// CAD TUTORIAL — after uploading a CAD file
// ═══════════════════════════════════════════════════════════════

export const cad_tutorial = [
  {
    title: 'CAD File Management',
    icon: <CloudUpload sx={{ fontSize: 28 }} />,
    color: '#0288d1',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Design / CAD</strong> section has two panels: a <strong>file list</strong> on the left and a <strong>viewer</strong> on the right.
        </Typography>
        <FeatureRow icon={<CloudUpload />} title="File List (Left Panel)" color="#0288d1"
          items={['Browse, search, and filter all CAD files in your project', 'Upload new files (.dwg, .dxf, .step, .prt, .asm, .stp, .igs)', 'Upload new versions of existing files with change descriptions', 'Multi-select files for bulk operations']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Visibility />} title="Click a File to Inspect" color="#667eea"
          items={['Detail drawer slides out: Overview, History, Compare tabs', 'Overview: file metadata, version, branch, creation date', 'History: every version snapshot with timestamps', 'Compare: side-by-side diff between any two versions', '"Open in Viewer" button loads the file in the 3D/2D viewer']} />
      </>
    ),
  },
  {
    title: 'Viewer Toolbar',
    icon: <ViewInAr sx={{ fontSize: 28 }} />,
    color: '#0288d1',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Once a file is loaded in the viewer, the toolbar gives you powerful inspection tools:
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <FeatureCard icon={<Straighten />} title="Measure" desc="Measure distances, angles, and areas directly on the model" color="#0288d1" />
          <FeatureCard icon={<Draw />} title="Markup" desc="Add redline annotations, text, arrows, and shapes for review" color="#f44336" />
          <FeatureCard icon={<ContentCut />} title="Section Plane" desc="Cut through 3D models to inspect internal geometry" color="#ff9800" />
          <FeatureCard icon={<Fullscreen />} title="Fullscreen" desc="Expand to fill the screen — chat stays visible on the right" color="#667eea" />
          <FeatureCard icon={<Download />} title="Download" desc="Download the source file (STEP, DWG, DXF) of the current drawing" color="#11998e" />
          <FeatureCard icon={<TableChart />} title="BOM Extraction" desc="AI extracts Bill of Materials — parts, quantities, categories" color="#764ba2" />
        </Box>
      </>
    ),
  },
  {
    title: 'CAD Agent — AI Drawing Generation',
    icon: <SmartToy sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Switch e-pilot to <strong>CAD Mode</strong> (toggle in the chat header) to generate and modify drawings with AI:
        </Typography>
        <FeatureRow icon={<SmartToy />} title="Generate Drawings" color="#764ba2"
          items={['Describe what you need: "Draw a rail cross-section with UIC60 profile"', 'AI generates DXF drawings using engineering knowledge', 'Railway KB: 25 rail profiles, 16 fastenings, 15 turnouts, 10 gauges', 'Generated drawings appear in the viewer automatically']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Edit />} title="Modify Existing Drawings" color="#11998e"
          items={['Ask e-pilot to modify: "Add dimensions" or "Change the gauge to 1435mm"', 'AI loads the drawing spec and applies targeted edits', 'Every modification creates a new version — original is preserved']} />
        <Tip text='CAD mode requires a project to be selected. e-pilot uses your project objectives and Knowledge Base for context.' />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// CHAT / E-PILOT TUTORIAL — after asking e-pilot a question
// ═══════════════════════════════════════════════════════════════

export const chat_tutorial = [
  {
    title: 'e-pilot AI Assistant',
    icon: <Chat sx={{ fontSize: 28 }} />,
    color: '#667eea',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          e-pilot is your AI engineering assistant. It's always available in the right sidebar and understands your project context.
        </Typography>
        <FeatureRow icon={<Chat />} title="Two Modes" color="#667eea"
          items={['General Mode: ask questions, analyze files, get engineering advice', 'CAD Mode: generate and modify CAD drawings with AI (toggle in header)', 'Mode toggle is in the chat header — look for the CAD icon']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<AttachFile />} title="File Attachments" color="#764ba2"
          items={['Click the attachment icon to include files as context', 'Attach requirements, documents, compliance standards, or CAD files', 'e-pilot reads the file content to give informed answers', 'Requirement pseudo-documents (req:*) are supported too']} />
      </>
    ),
  },
  {
    title: 'How e-pilot Uses Context',
    icon: <Psychology sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          e-pilot gets smarter the more context you provide. Here's what it reads:
        </Typography>
        <FeatureRow icon={<Lightbulb />} title="Automatic Context" color="#11998e"
          items={['Project objectives & description (set in Project Home)', 'Milestone status and deadlines', 'Past conversation history (auto-summarized after 30 messages)', 'Similar past conversations (RAG search via embeddings)']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<FolderSpecial />} title="Knowledge Base" color="#667eea"
          items={['Documents, CAD files, requirements, and standards pinned to the KB', 'Items with context enabled are always available to e-pilot', 'Works like permanent file attachments — persists across sessions', 'The more you pin, the better e-pilot understands your project']} />
        <Tip text='Set clear project objectives and pin key reference docs to the KB for the best AI responses.' />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// REQUIREMENTS TUTORIAL — after creating a requirement
// ═══════════════════════════════════════════════════════════════

export const requirements_tutorial = [
  {
    title: 'Requirements Management',
    icon: <Checklist sx={{ fontSize: 28 }} />,
    color: '#11998e',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Requirements</strong> section has two tabs for managing and importing requirements:
        </Typography>
        <FeatureRow icon={<Checklist />} title="Requirements List (Tab 1)" color="#11998e"
          items={['Paginated table with search and status/method filters', 'Columns: ID, Description, Verification Method, Status, Actions', 'Status options: Open, Verified, Failed, Waived', 'Verification methods: Test, Analysis, Inspection, Demonstration', 'Multi-select rows for bulk delete operations']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Edit />} title="Create / Edit Dialog" color="#667eea"
          items={['Description field with full text support', 'Select verification method and status', 'Optional: link to a part ID for traceability', 'Attach a reference document (PDF, DOCX, XLS, images, max 50MB)', 'Drag-and-drop file upload supported']} />
      </>
    ),
  },
  {
    title: 'Detail Drawer & History',
    icon: <History sx={{ fontSize: 28 }} />,
    color: '#667eea',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Click any requirement row to open the <strong>detail drawer</strong> with three tabs:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TabCard label="Overview" color="#667eea" desc="Full requirement details, metadata, attached file preview, version info" />
          <TabCard label="History" color="#764ba2" desc="Every version snapshot — who changed what and when. AI-powered diff between versions." />
          <TabCard label="Compare" color="#11998e" desc="Side-by-side comparison of any two versions with AI-generated change summary" />
        </Box>
        <Tip text='History tracks every edit automatically. Use Compare to understand exactly what changed between versions.' />
      </>
    ),
  },
  {
    title: 'AI Document Analysis',
    icon: <DocumentScanner sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Document Analysis</strong> tab (Tab 2) uses AI to extract requirements from uploaded documents:
        </Typography>
        <FeatureRow icon={<DocumentScanner />} title="How It Works" color="#764ba2"
          items={['Select a document from your project files', 'Choose analysis type: Summary, Requirements, Technical, or Full Analysis', 'Select output language (20+ languages supported)', 'AI reads the document and extracts structured content']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<AutoAwesome />} title="What You Get" color="#11998e"
          items={['Extracted sections with titles and content', 'Structured requirements table (ID + description)', 'Download the analysis as a .docx file', '"Requirements" mode: bulk-import extracted requirements directly into your project']} />
        <Tip text='Upload a client spec PDF and use "Requirements" analysis to auto-populate your requirements list in seconds.' />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// DOCUMENTS TUTORIAL — after uploading a document
// ═══════════════════════════════════════════════════════════════

export const documents_tutorial = [
  {
    title: 'Document Management',
    icon: <Description sx={{ fontSize: 28 }} />,
    color: '#1565c0',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Documents</strong> section manages all project documents with version control and AI capabilities:
        </Typography>
        <FeatureRow icon={<Description />} title="Document List (Tab 1)" color="#1565c0"
          items={['Paginated table with search and type filter', 'Types: Specification, Requirement, Test Plan, Test Report, Drawing, Datasheet, Manual, Other', 'Status tracking: Draft → Under Review → Released → Obsolete', 'Upload new versions with change descriptions', 'Multi-select for bulk operations']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<CloudUpload />} title="Upload Dialog" color="#667eea"
          items={['Title, type, status, and description fields', 'Select target branch (changes are branch-specific)', 'Optional: link to a part ID for traceability', 'Accepts PDF, DOCX, JPG, PNG files']} />
      </>
    ),
  },
  {
    title: 'Detail Drawer',
    icon: <ManageSearch sx={{ fontSize: 28 }} />,
    color: '#667eea',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Click any document row to open the detail drawer with <strong>four tabs</strong>:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TabCard label="Overview" color="#667eea" desc="Title, type, status, description, version, branch, and creation details" />
          <TabCard label="History" color="#764ba2" desc="Full version history with timestamps — see every edit and upload" />
          <TabCard label="Compare" color="#11998e" desc="AI-powered side-by-side comparison between any two document versions" />
          <TabCard label="Reports" color="#ff9800" desc="AI-generated reports linked to this document (from the AI Generation tab)" />
        </Box>
      </>
    ),
  },
  {
    title: 'AI Document Generation',
    icon: <AutoAwesome sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>AI Generation</strong> tab (Tab 2) creates professional documents from your project data:
        </Typography>
        <FeatureRow icon={<PlayArrow />} title="3-Step Wizard" color="#764ba2"
          items={['Step 1 — Select Source: pick an uploaded document as input', 'Step 2 — Choose Template: PQP, User Manual, Verification Sheet (ISO 17025), Test Report (ISO 17025), or Inspection Plan (ISO 2859-1)', 'Step 3 — Review & Approve: edit, add notes, then approve or reject']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Download />} title="Output" color="#11998e"
          items={['Generated documents available as .docx download', 'Approved reports are linked to the source document (visible in Reports tab)', 'Rejected reports are discarded — generate again with different settings']} />
        <Tip text='Use AI generation to create ISO-compliant test reports and inspection plans from your specifications.' />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// COMPLIANCE TUTORIAL — after running a compliance check
// ═══════════════════════════════════════════════════════════════

export const compliance_tutorial = [
  {
    title: 'Compliance Standards',
    icon: <Gavel sx={{ fontSize: 28 }} />,
    color: '#e65100',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Compliance</strong> section has four tabs for managing standards and tracking compliance:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TabCard label="Standards" color="#e65100" desc="Create and manage compliance standards with file attachments (PDFs, specs)" />
          <TabCard label="Traceability" color="#667eea" desc="Clause-by-clause evidence coverage — which standard clauses have linked documents, requirements, or V&V" />
          <TabCard label="Dashboard" color="#11998e" desc="Project-wide compliance results: pass/fail/warning across all drawings" />
          <TabCard label="Audit Log" color="#764ba2" desc="History of all compliance checks, manual overrides, and assignments" />
        </Box>
      </>
    ),
  },
  {
    title: 'Compliance Dashboard',
    icon: <Dashboard sx={{ fontSize: 28 }} />,
    color: '#11998e',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Dashboard</strong> tab gives you a bird's-eye view of compliance status:
        </Typography>
        <FeatureRow icon={<Assessment />} title="Summary Cards" color="#11998e"
          items={['Total drawings checked, passed (green), failed (red), warnings (orange), unchecked (gray)', 'Effective status accounts for both AI checks and manual overrides']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<FactCheck />} title="Drawings Table" color="#667eea"
          items={['Click any drawing row to see its compliance verdicts', 'Each verdict: requirement/standard checked, AI result, confidence score', 'Manual Override: set pass/fail/warning per drawing regardless of AI', '"Checked Against": assign which requirements and standards apply to each drawing', 'Source column shows REQ (requirement) or STD (standard) for each verdict']} />
      </>
    ),
  },
  {
    title: 'AI Compliance Checking',
    icon: <VerifiedUser sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          AI compliance checking happens from the <strong>CAD file detail drawer</strong> (Compliance tab):
        </Typography>
        <FeatureRow icon={<SmartToy />} title="How It Works" color="#764ba2"
          items={['Open a CAD file → click the Compliance tab in the detail drawer', 'Select which requirements and/or standards to check against', 'AI analyzes the drawing image + extracted data against each requirement', 'Streaming results: progress updates as each check completes', 'Verdicts: Pass, Fail, Warning, or Not Applicable — with confidence scores']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Download />} title="Export & Track" color="#11998e"
          items={['Export results to Excel or PDF', 'Results are saved to the drawing and appear in the Compliance Dashboard', 'Run checks again anytime — new results replace old ones']} />
        <Tip text='For best results, attach detailed requirement documents and compliance standard PDFs with clear acceptance criteria.' />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// KNOWLEDGE BASE TUTORIAL — after explore_kb step
// ═══════════════════════════════════════════════════════════════

export const verification_tutorial = [
  {
    title: 'Verification Overview',
    content: (
      <>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The <strong>Verification</strong> section manages design gates and test cases:
        </Typography>
        <FeatureRow icon={<FactCheck />} title="Design Gates" color="#667eea"
          items={['Gate checklists with progress tracking', 'Approve gates when all checks pass', 'Reset gates to restart the review process', 'Overview shows requirements verified vs. gates passed']} />
        <Box sx={{ mt: 1.5 }} />
        <FeatureRow icon={<Checklist />} title="Test Management" color="#22c55e"
          items={['Create test cases with type, priority, and linked requirements', 'Define multi-step test procedures with expected results', 'Track status: Draft, Ready, In Progress, Passed, Failed, Blocked', 'Quick actions to mark tests as Passed or Failed']} />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════

export const knowledge_base_tutorial = [
  {
    title: 'Knowledge Base Overview',
    icon: <LibraryBooks sx={{ fontSize: 28 }} />,
    color: '#667eea',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Knowledge Base</strong> is the permanent memory for your project's e-pilot. Everything pinned here is always available as AI context.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TabCard label="Documents" color="#667eea" desc="Specs, manuals, reports — uploaded documents pinned for AI context" />
          <TabCard label="CAD" color="#0288d1" desc="CAD files and drawings available for AI-powered analysis" />
          <TabCard label="Requirements" color="#2e7d32" desc="Engineering requirements with attached reference documents" />
          <TabCard label="Compliance" color="#e65100" desc="Compliance standards pinned for automated checking" />
        </Box>
        <Tip text='Items are automatically added when you upload files to the project. You can toggle which ones e-pilot uses.' />
      </>
    ),
  },
  {
    title: 'How KB Powers e-pilot',
    icon: <Hub sx={{ fontSize: 28 }} />,
    color: '#764ba2',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The Knowledge Base is what makes e-pilot understand <strong>your specific project</strong>:
        </Typography>
        <FeatureRow icon={<DataObject />} title="Context Toggle" color="#667eea"
          items={['Each item has a blue/gray context icon', 'Blue = enabled — e-pilot can read this file when answering questions', 'Gray = disabled — file exists in KB but not used as AI context', 'Toggle anytime without deleting the file']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Speed />} title="Indexing & Chunks" color="#11998e"
          items={['Files are split into "chunks" for efficient AI search', 'Status indicators: Ready, Indexing, Pending, Failed', 'Chunk count shows how many segments the file was split into', 'Processing bar appears when files are being indexed']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<Psychology />} title="vs. Chat Attachments" color="#764ba2"
          items={['Chat attachments: temporary context for one conversation', 'Knowledge Base: permanent context across all conversations', 'Pin your most important references to KB, attach specific files to individual chats', 'KB + objectives + milestones = e-pilot\'s full understanding of your project']} />
      </>
    ),
  },
];

// ═══════════════════════════════════════════════════════════════
// ORCHESTRATOR TUTORIAL — after building a pipeline
// ═══════════════════════════════════════════════════════════════

export const orchestrator_tutorial = [
  {
    title: 'Engineering Orchestrator',
    icon: <AccountTree sx={{ fontSize: 28 }} />,
    color: '#667eea',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          The <strong>Orchestrator</strong> lets you build visual pipelines connecting your engineering tools:
        </Typography>
        <FeatureRow icon={<Settings />} title="Software Dock (Left Panel)" color="#667eea"
          items={['Your selected engineering tools (AutoCAD, SolidWorks, ANSYS, etc.)', 'Drag a tool onto the canvas to add it to your pipeline', 'Click the gear icon to customize which tools appear', 'Tool selection syncs across devices via your profile']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<AccountTree />} title="Pipeline Canvas" color="#764ba2"
          items={['Drag tools from the dock to create pipeline nodes', 'Connect nodes by dragging from output handles to input handles', 'Each connection defines data flow between tools', 'Clear button removes all nodes and connections']} />
      </>
    ),
  },
  {
    title: 'Generate & Execute Plans',
    icon: <PlayArrow sx={{ fontSize: 28 }} />,
    color: '#11998e',
    content: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
          Once your pipeline is set up, AI generates and executes an engineering plan:
        </Typography>
        <FeatureRow icon={<AutoAwesome />} title="Plan Generation" color="#11998e"
          items={['Click "Generate Plan" to have AI create an execution strategy', 'AI analyzes the pipeline topology and tool capabilities', 'Review the plan before execution — edit or regenerate if needed']} />
        <Box sx={{ mt: 1 }} />
        <FeatureRow icon={<PlayArrow />} title="Execution" color="#667eea"
          items={['Pipeline executes step-by-step with real-time status updates', 'Each node shows: running (blue), completed (green), or failed (red)', 'Streaming NDJSON progress — see results as each step finishes', 'Final results panel auto-expands with the complete execution summary']} />
        <Tip text='Start simple — connect 2-3 tools and iterate. Complex pipelines work best when you build them incrementally.' />
      </>
    ),
  },
];
